import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ManagementClipResponse, ManagementMediumResponse, ManagementTranscriptionControllerService, ManagementUploadControllerService, ResourceRequest } from 'api-clipping';
import {
  	ManagementContractNoticeResponse,
	ManagementContractAnnouncementResponse,
	ManagementUploadControllerService as ManagementContractNoticesUploadControllerService,
	ManagementSaleResponse,
	ResourceRequest as ResourceRequestContractNotices,
} from 'api-contract-notices';
import { Subject } from 'rxjs/internal/Subject';
import { take } from 'rxjs/internal/operators/take';
import { timeout } from 'rxjs/internal/operators/timeout';
import { firstValueFrom, TimeoutError } from 'rxjs';
import { SharedService } from './shared.service';
import { TranslateService } from '@ngx-translate/core';

type UploadResourceType = 'IMAGE' | 'VIDEO' | 'SCREENSHOT' | 'NOTICE' | 'ANNOUNCEMENT' | 'SALE' | 'BANKRUPTCY' | 'MEDIUM';
type UploadModule = 'CLIPPING' | 'CONTRACT_NOTICES';
type UploadFailureReasonKey = 'NETWORK' | 'TIMEOUT' | 'EXPIRED_LINK' | 'SERVER_BUSY' | 'CLIENT_ERROR' | 'UNKNOWN';

type UploadableItem = ManagementClipResponse | ManagementContractNoticeResponse | ManagementContractAnnouncementResponse | ManagementSaleResponse | ManagementMediumResponse;

interface UploadTask {
	id: string;
	file: File;
	uploadLink: string;
	type: UploadResourceType;
	module: UploadModule;
	item: UploadableItem;
	autoTranscribe?: boolean;
	transcriptionLang?: string;
	url?: string;
	attempt: number;
}

export interface FailedUpload {
	id: string;
	name: string;
	reasonKey: UploadFailureReasonKey;
	status?: number;
	attempts: number;
}

interface UploadErrorClassification {
	retryable: boolean;
	reasonKey: UploadFailureReasonKey;
	status?: number;
}

@Injectable({
	providedIn: 'root',
})
export class UploadManagementService {
	linkGenerated: Subject<string> = new Subject();

	fileQueued: any;
	filesUploading = new Map<string, any>();
	filesUploaded: any[] = [];
	filesUnsuccessful: FailedUpload[] = [];

	// B2 occasionally answers busy/offline with 5xx/408/429, or drops the connection outright
	// (status 0), and a presigned PUT URL is only valid for 10 minutes (B2ServiceImpl) - long
	// enough for one video normally, but not guaranteed across several retries. So every retry
	// re-requests a fresh URL and only retryable failures get retried.
	private readonly MAX_CONCURRENT_UPLOADS = 3;
	// "up to 3 attempts" worth of retrying = 1 initial try + 3 retries, 4 tries total; the three
	// backoff values below are the pauses *before* each of those 3 retries.
	private readonly MAX_UPLOAD_ATTEMPTS = 4;
	private readonly RETRY_DELAYS_MS = [1000, 3000, 7000];
	private readonly UPLOAD_ATTEMPT_TIMEOUT_MS = 5 * 60 * 1000;

	private uploadQueue: UploadTask[] = [];
	private activeUploads = 0;

	constructor(
		private apiClippingUploadController: ManagementUploadControllerService,
		private apiClippingTranscription: ManagementTranscriptionControllerService,
		private apiContractNoticeUploadController: ManagementContractNoticesUploadControllerService,
		private http: HttpClient,
		private sharedService: SharedService,
		private translate: TranslateService
	) {}

	generatePresignedUrl(file: File, type: UploadResourceType, module: UploadModule) {
		this.fileQueued = null;

		if (module === 'CLIPPING') {
			const uploadDto: ResourceRequest = {
				filename: file.name,
				contentType: file.type,
				type: (<any>ResourceRequest.TypeEnum)[type],
				fileSize: file.size,
			};
			this.apiClippingUploadController
				.generatePreSignedUrls([uploadDto])
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.linkGenerated.next(response[0].cdnUrl || '');
						this.fileQueued = {
							clipId: '',
							uploadLink: response[0].uploadUrl,
							file,
							type,
							module,
						};
					},
					error: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
							type: 'ERROR',
						});
					},
				});
		} else {
			const uploadDto: ResourceRequestContractNotices = {
				filename: file.name,
				contentType: file.type,
				type: (<any>ResourceRequestContractNotices.TypeEnum)[type],
				fileSize: file.size,
			};

			this.apiContractNoticeUploadController
				.generatePreSignedUrls([uploadDto])
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.linkGenerated.next(response[0].cdnUrl || '');
						this.fileQueued = {
							clipId: '',
							uploadLink: response[0].uploadUrl,
							file,
							type,
							module,
						};
					},
					error: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
							type: 'ERROR',
						});
					},
				});
		}
	}

	// Requests a brand-new presigned URL for the same file/type/module without touching the
	// single-slot `fileQueued` (which belongs to whatever file is currently selected in the
	// form) - used to refresh an about-to-retry upload's link in place.
	private requestPresignedUrl(file: File, type: UploadResourceType, module: UploadModule): Promise<{ uploadUrl?: string; cdnUrl?: string }> {
		if (module === 'CLIPPING') {
			const uploadDto: ResourceRequest = {
				filename: file.name,
				contentType: file.type,
				type: (<any>ResourceRequest.TypeEnum)[type],
				fileSize: file.size,
			};
			return firstValueFrom(this.apiClippingUploadController.generatePreSignedUrls([uploadDto])).then(response => response[0]);
		}

		const uploadDto: ResourceRequestContractNotices = {
			filename: file.name,
			contentType: file.type,
			type: (<any>ResourceRequestContractNotices.TypeEnum)[type],
			fileSize: file.size,
		};
		return firstValueFrom(this.apiContractNoticeUploadController.generatePreSignedUrls([uploadDto])).then(response => response[0]);
	}

	// Queues the upload instead of firing it immediately - several clips created back-to-back
	// (e.g. a batch of TV videos) used to all PUT to B2 at once with no cap, which is exactly the
	// kind of load that makes B2 answer 503/500 or drop the connection.
	uploadFile(item: UploadableItem, autoTranscribe?: boolean, transcriptionLang?: string) {
		if (!this.fileQueued || !this.fileQueued.uploadLink) {
			return;
		}

		let url: string | undefined;
		if (<ManagementClipResponse>item || <ManagementMediumResponse>item) {
			url = (item as ManagementClipResponse).mediaUrl;
		} else if (<ManagementContractNoticeResponse>item) {
			url = (item as ManagementContractNoticeResponse).fileUrl;
		} else if (<ManagementContractAnnouncementResponse>item) {
			url = (item as ManagementContractAnnouncementResponse).fileUrl;
		} else if (<ManagementSaleResponse>item) {
			url = (item as ManagementSaleResponse).fileUrl;
		}

		// Captured now, at call time - not read back from `this.fileQueued` later - so picking the
		// next file for the next clip while this one is still queued/uploading can't swap it out
		// from under an in-flight task.
		const task: UploadTask = {
			id: item.id as string,
			file: this.fileQueued.file,
			uploadLink: this.fileQueued.uploadLink,
			type: this.fileQueued.type,
			module: this.fileQueued.module,
			item,
			autoTranscribe,
			transcriptionLang,
			url,
			attempt: 0,
		};

		this.filesUploading.set(task.id, {
			id: task.id,
			name: task.file.name,
			autoTranscribe,
			transcriptionLang,
			uploadProgress: 0,
			url,
		});

		this.uploadQueue.push(task);
		this.processUploadQueue();
	}

	private processUploadQueue(): void {
		while (this.activeUploads < this.MAX_CONCURRENT_UPLOADS && this.uploadQueue.length > 0) {
			const task = this.uploadQueue.shift() as UploadTask;
			this.activeUploads++;
			this.runUploadTask(task);
		}
	}

	private async runUploadTask(task: UploadTask): Promise<void> {
		try {
			await this.uploadWithRetries(task);
		} finally {
			this.activeUploads--;
			this.processUploadQueue();
		}
	}

	private async uploadWithRetries(task: UploadTask): Promise<void> {
		for (task.attempt = 1; task.attempt <= this.MAX_UPLOAD_ATTEMPTS; task.attempt++) {
			try {
				await this.performUploadAttempt(task);
				return;
			} catch (error) {
				const classification = this.classifyUploadError(error);
				const hasMoreAttempts = task.attempt < this.MAX_UPLOAD_ATTEMPTS;

				if (!classification.retryable || !hasMoreAttempts) {
					this.markUploadFailed(task, classification);
					return;
				}

				// Always ask for a fresh link before retrying - cheap, and covers the case where the
				// 10-minute presigned URL expired while this attempt (or an earlier one) was running.
				await this.refreshUploadLink(task);
				await this.delay(this.RETRY_DELAYS_MS[task.attempt - 1] ?? this.RETRY_DELAYS_MS[this.RETRY_DELAYS_MS.length - 1]);
			}
		}
	}

	private performUploadAttempt(task: UploadTask): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const reader = new FileReader();
			reader.onerror = () => reject(reader.error || new Error('file-read-error'));
			reader.onload = (event: any) => {
				const content = event.target.result;
				const headers = new HttpHeaders({ 'Content-Type': task.file.type });
				this.http
					.put(task.uploadLink, content, {
						headers,
						reportProgress: true,
						observe: 'events',
					})
					.pipe(timeout(this.UPLOAD_ATTEMPT_TIMEOUT_MS))
					.subscribe({
						next: (response: any) => {
							if (response.type === HttpEventType.UploadProgress && response.total) {
								const previousData = this.filesUploading.get(task.id);
								this.filesUploading.set(task.id, { ...previousData, uploadProgress: Math.round(100 * (response.loaded / response.total)) });
							} else if (response.ok) {
								this.finishUploadSuccess(task);
								resolve();
							}
						},
						error: (err: any) => reject(err),
					});
			};
			reader.readAsArrayBuffer(task.file);
		});
	}

	private finishUploadSuccess(task: UploadTask): void {
		const previousData = this.filesUploading.get(task.id);
		this.filesUploading.delete(task.id);
		this.filesUploaded.push(previousData);
		if (task.autoTranscribe && task.item) {
			this.sendUploadCompleteRequest(task.item as ManagementClipResponse, task.transcriptionLang);
		}
	}

	private markUploadFailed(task: UploadTask, classification: UploadErrorClassification): void {
		this.filesUploading.delete(task.id);
		this.filesUnsuccessful.push({
			id: task.id,
			name: task.file.name,
			reasonKey: classification.reasonKey,
			status: classification.status,
			attempts: task.attempt,
		});
	}

	private async refreshUploadLink(task: UploadTask): Promise<void> {
		try {
			const response = await this.requestPresignedUrl(task.file, task.type, task.module);
			if (response && response.uploadUrl) {
				task.uploadLink = response.uploadUrl;
			}
		} catch {
			// Keep the previous link - the next attempt will fail fast and get classified normally.
		}
	}

	private delay(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	// Network drops and 403/408/429/5xx are exactly what B2 does when it is busy, offline, or has
	// invalidated a presigned URL - all worth retrying. Anything else (e.g. a genuine 400) will not
	// be fixed by trying again.
	private classifyUploadError(error: any): UploadErrorClassification {
		if (error instanceof TimeoutError) {
			return { retryable: true, reasonKey: 'TIMEOUT' };
		}

		const status = error instanceof HttpErrorResponse ? error.status : typeof error?.status === 'number' ? error.status : undefined;

		if (status === 0) {
			return { retryable: true, reasonKey: 'NETWORK' };
		}
		if (status === 403) {
			return { retryable: true, reasonKey: 'EXPIRED_LINK', status };
		}
		if (status === 408 || status === 429 || (status !== undefined && status >= 500 && status < 600)) {
			return { retryable: true, reasonKey: 'SERVER_BUSY', status };
		}
		if (status !== undefined) {
			return { retryable: false, reasonKey: 'CLIENT_ERROR', status };
		}
		return { retryable: false, reasonKey: 'UNKNOWN', status };
	}

	sendUploadCompleteRequest(clip: ManagementClipResponse, transcriptionLang?: string) {
		const fileUploading = this.filesUploading.get(clip.id);
		if (fileUploading) {
			this.filesUploading.set(clip.id, { ...fileUploading, autoTranscribe: true, transcriptionLang, url: clip.mediaUrl });
		} else {
			const uploadedFile = this.filesUploaded.find(item => item.id === clip.id);

			this.apiClippingTranscription
				.transcribe({
					clipId: clip.id,
					lang: (transcriptionLang || 'MK') as any,
					url: clip.mediaUrl ? clip.mediaUrl : uploadedFile.url,
				})
				.pipe(take(1))
				.subscribe({
					next: () => {
						// transcription queued
					},
					error: () => {
						// transcription error
					},
				});
		}
	}
}
